import { z } from 'zod';

import { COUNTRY_MESSAGE, resolveCountryName } from './countryCatalog';
import {
  isMeaningfulReason,
  isMeaningfulText,
  isPersonName,
  isPlaceName,
  isWebUrl,
  REASON_MESSAGE,
  WEB_URL_MAX_LENGTH,
  WEB_URL_MESSAGE,
  withHttps,
} from './fieldRules';

/**
 * Zod spellings of the shared field rules (`fieldRules.ts`), one per kind of field, each carrying
 * the message the server returns for the same rule (`FieldRuleExtensions` in the Application layer).
 * Required-ness is part of the builder's name rather than a flag, so a schema reads as what the
 * field is.
 */

const tooLong = (label: string, max: number) => `${label} must be ${max} characters or fewer`;

/**
 * The value an EDIT form loaded for this field — what the record already holds. ADR-0038 §4: a rule
 * judges a value being written, never one already stored, so an edit form passes the stored value and
 * a field the person left untouched is accepted as it is (and submitted as it is — not re-formatted).
 * A changed value meets the rule in full. New-record forms pass nothing.
 */
export type StoredValue = string | null | undefined;

/** `EditedValue.IsUnchanged` on the server: surrounding whitespace ignored, blank equals absent. */
export const isUnchanged = (value: string, stored: StoredValue): boolean =>
  stored !== undefined && value.trim() === (stored ?? '').trim();

/** A required name or title: trimmed, non-blank, with at least one letter or digit ("C++ Developer" yes, "!!!" no). */
export const requiredText = (label: string, max: number, stored?: StoredValue) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(max, tooLong(label, max))
    .refine((v) => isUnchanged(v, stored) || isMeaningfulText(v), `${label} must contain letters or numbers`);

/** An optional name or title: blank, or as {@link requiredText}. */
export const optionalText = (label: string, max: number, stored?: StoredValue) =>
  z
    .string()
    .trim()
    .max(max, tooLong(label, max))
    .refine(
      (v) => v === '' || isUnchanged(v, stored) || isMeaningfulText(v),
      `${label} must contain letters or numbers`,
    );

/** A required person's name ("Anne-Marie", "O'Brien", "José"; not "12345"). */
export const personName = (label: string, max: number) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(max, tooLong(label, max))
    .refine(isPersonName, `${label} may contain letters, spaces, apostrophes, hyphens and periods only`);

/** An optional city or state ("São Paulo", "Stratford-upon-Avon"; not "12345"). */
export const optionalPlace = (label: string, max: number, stored?: StoredValue) =>
  z
    .string()
    .trim()
    .max(max, tooLong(label, max))
    .refine((v) => v === '' || isUnchanged(v, stored) || isPlaceName(v), `${label} must be a place name`);

/**
 * An optional country from the catalog, resolved to its canonical spelling ("india", "IN" → "India")
 * so the form submits exactly what the server will store.
 */
export const optionalCountry = (stored?: StoredValue) =>
  z
    .string()
    .trim()
    .max(100, tooLong('Country', 100))
    // An unchanged stored country is submitted exactly as stored — not canonicalized — so saving an
    // unrelated field never rewrites it.
    .transform((v) => (v === '' || isUnchanged(v, stored) ? v : (resolveCountryName(v) ?? v)))
    .refine((v) => v === '' || isUnchanged(v, stored) || resolveCountryName(v) !== undefined, COUNTRY_MESSAGE);

/** As {@link optionalCountry}, but a country must be given. */
export const requiredCountry = () =>
  z
    .string()
    .trim()
    .min(1, 'Country is required')
    .transform((v) => resolveCountryName(v) ?? v)
    .refine((v) => resolveCountryName(v) !== undefined, COUNTRY_MESSAGE);

/** An optional web link; a bare domain gets https:// so "acme.com" is accepted as typed. */
export const optionalWebUrl = (stored?: StoredValue) =>
  z
    .string()
    .trim()
    .transform((v) => (v === '' || isUnchanged(v, stored) ? v : withHttps(v)))
    .pipe(
      z
        .string()
        .max(WEB_URL_MAX_LENGTH, tooLong('URL', WEB_URL_MAX_LENGTH))
        .refine((v) => v === '' || isUnchanged(v, stored) || isWebUrl(v), WEB_URL_MESSAGE),
    );

/** As {@link optionalWebUrl}, but a link must be given. */
export const requiredWebUrl = (label = 'URL', stored?: StoredValue) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .transform((v) => (isUnchanged(v, stored) ? v : withHttps(v)))
    .pipe(
      z
        .string()
        .max(WEB_URL_MAX_LENGTH, tooLong(label, WEB_URL_MAX_LENGTH))
        .refine((v) => isUnchanged(v, stored) || isWebUrl(v), WEB_URL_MESSAGE),
    );

/** A required explanation someone else will read ("Spam" yes, "." no). */
export const requiredReason = (label: string, max: number) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(max, tooLong(label, max))
    .refine(isMeaningfulReason, REASON_MESSAGE);

/** An optional explanation: blank, or as {@link requiredReason}. */
export const optionalReason = (label: string, max: number) =>
  z
    .string()
    .trim()
    .max(max, tooLong(label, max))
    .refine((v) => v === '' || isMeaningfulReason(v), REASON_MESSAGE);
